import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { track } from '../lib/analytics';

// Module-level guard: survives AuthCallback re-mounts triggered by AuthProvider re-renders.
// Emergent's session_id is single-use — the second exchange would 404, falsely signaling failure.
const processedSessionIds = new Set();

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    // Module-level dedupe: if this session_id was already processed in this tab session,
    // skip the redundant exchange — refresh AuthContext and route as authenticated.
    if (processedSessionIds.has(sessionId)) {
      (async () => {
        const hydrated = await refresh();
        const intended = sessionStorage.getItem('post_login_redirect');
        sessionStorage.removeItem('post_login_redirect');
        const isAdmin = Boolean(hydrated?.isAdmin);
        let dest;
        if (intended && intended.startsWith('/') && !intended.startsWith('//')) {
          dest = intended.startsWith('/admin') && !isAdmin ? '/dashboard' : intended;
        } else {
          dest = '/dashboard';
        }
        window.history.replaceState({}, document.title, dest);
        navigate(dest, { replace: true });
      })();
      return;
    }
    processedSessionIds.add(sessionId);

    (async () => {
      try {
        const { data } = await api.post('/auth/session', null, {
          headers: { 'X-Session-ID': sessionId },
        });

        // Session cookie is now set. From this point, the user IS authenticated.
        // Any subsequent failure (e.g. refresh()) must NOT bounce them to /login —
        // a stale React state is recoverable; an unnecessary /login redirect is not.
        let hydrated = null;
        try {
          hydrated = await refresh();
        } catch (refreshErr) {
          // eslint-disable-next-line no-console
          console.warn('[auth/callback] refresh failed post-session (non-fatal)', refreshErr);
        }

        // Resolve destination — prefer the user's pre-login intent.
        const intended = sessionStorage.getItem('post_login_redirect');
        sessionStorage.removeItem('post_login_redirect');

        const isAdmin = Boolean(hydrated?.isAdmin ?? data?.is_admin);

        let dest;
        if (intended && intended.startsWith('/') && !intended.startsWith('//')) {
          dest = intended.startsWith('/admin') && !isAdmin ? '/dashboard' : intended;
        } else {
          // Always land on /dashboard after a successful login. Admins can click "Admin" in the navbar.
          dest = '/dashboard';
        }

        // Clean URL fragment + replace history entry
        window.history.replaceState({}, document.title, dest);
        // Fire analytics — `created` field comes from /auth/session when user is new vs returning.
        track(data?.created ? 'signup_complete' : 'login_complete', {
          provider: data?.provider || 'google',
          is_admin: !!(hydrated?.isAdmin ?? data?.is_admin),
        });
        toast.success(`Welcome${data?.name ? `, ${data.name.split(' ')[0]}` : ''}.`);
        navigate(dest, { replace: true });
      } catch (err) {
        // Surface the BACKEND detail verbatim so production diagnosis doesn't require log access.
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || 'unknown_error';
        // eslint-disable-next-line no-console
        console.warn('[auth/callback] session exchange failed', { status, detail });
        const reason = detail === 'account_disabled' ? 'account_disabled' : detail;
        toast.error(`Sign-in failed: ${detail}`);
        navigate(`/login?reason=${encodeURIComponent(reason)}`, { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
      <div className="text-center">
        <div className="h-8 w-8 mx-auto border-2 border-[#d8b34a]/30 border-t-[#d8b34a] rounded-full animate-spin" />
        <p className="mt-4 text-sm text-zinc-400 font-mono tracking-wider">Completing sign-in…</p>
      </div>
    </div>
  );
}
